import { TaskStatus } from "fond/types/task";

import { apiSlice } from "./apiSlice";

type CustomWorkflow = {
  ID: string;
  Key: string;
};

type ListCustomWorkflowsResponse = {
  CustomWorkflows: CustomWorkflow[];
};

type CustomWorkflowExecution = {
  ID: string;
  Version: { ID: string };
  Status: TaskStatus;
  CustomWorkflow: CustomWorkflow;
  LogsStatus: "PENDING" | "COMPLETE";
  ErrorProcessingStatus: "NONE" | "IN_PROGRESS" | "COMPLETE";
};

type ListCustomWorkflowExecutionsResponse = {
  CustomWorkflowExecutions: CustomWorkflowExecution[];
};

export type WorkflowExecutionLog = {
  Filename: string;
  Content: string;
};

type WorkflowExecutionLogsResponse = {
  Logs: WorkflowExecutionLog[];
};

export const customWorkflowSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getCustomWorkflows: build.query<ListCustomWorkflowsResponse, string>({
      query: (versionId) => ({
        url: "/v2/custom-workflows",
        params: { version_id: versionId },
      }),
    }),
    getCustomWorkflowExecutions: build.query<ListCustomWorkflowExecutionsResponse, string>({
      query: (versionId) => ({
        url: "/v2/custom-workflow-executions",
        params: { version_id: versionId },
      }),
      providesTags: (result, error, versionId) => (result ? [{ type: "CustomWorkflowExecutions", id: versionId }] : []),
    }),
    executeCustomWorkflow: build.mutation<any, { versionId: string; workflowId: string; parameters?: object }>({
      query: ({ versionId, workflowId, parameters }) => ({
        url: `/v2/custom-workflow-executions`,
        method: "POST",
        body: { CustomWorkflowID: workflowId, VersionID: versionId, Parameters: parameters ?? null },
      }),
      invalidatesTags: (result, error, { versionId }) => [{ type: "CustomWorkflowExecutions", id: versionId }],
      onQueryStarted: async ({ versionId, workflowId }, { dispatch, queryFulfilled }) => {
        // Immediately evacuate any corresponding execution from the cache; the backend will create a new one.
        const patchResult = dispatch(
          customWorkflowSlice.util.updateQueryData("getCustomWorkflowExecutions", versionId, (executions) => {
            return {
              CustomWorkflowExecutions: executions.CustomWorkflowExecutions.filter(
                (execution) => execution.Version.ID !== versionId || execution.CustomWorkflow.ID !== workflowId
              ),
            };
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    getCustomWorkflowExecutionLogs: build.query<WorkflowExecutionLogsResponse, string>({
      query: (executionId) => `/v2/custom-workflow-executions/${executionId}/logs`,
    }),
    deleteCustomWorkflowExecution: build.mutation<void, { executionId: string; versionId: string }>({
      query: ({ executionId }) => ({
        url: `/v2/custom-workflow-executions/${executionId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { versionId }) => [{ type: "CustomWorkflowExecutions", id: versionId }],
      onQueryStarted: async ({ executionId, versionId }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          customWorkflowSlice.util.updateQueryData("getCustomWorkflowExecutions", versionId, (executions) => {
            return {
              CustomWorkflowExecutions: executions.CustomWorkflowExecutions.filter((execution) => execution.ID !== executionId),
            };
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});

/**
 * Endpoint hooks
 */
export const {
  useGetCustomWorkflowsQuery,
  useGetCustomWorkflowExecutionsQuery,
  useExecuteCustomWorkflowMutation,
  useGetCustomWorkflowExecutionLogsQuery,
  useDeleteCustomWorkflowExecutionMutation,
} = customWorkflowSlice;
